import React, { useState, useEffect } from 'react';
import { FaBuilding, FaPlus, FaTimes, FaLayerGroup } from 'react-icons/fa';
import './HospitalFloorsModal.css';
import AddFloorModal from './AddFloorModal';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';

const HospitalFloorsModal = ({
  isOpen,
  onClose,
  floors = [],
  rooms = [],
  roomTypes = [],
  bedTypes = [],
  onAddFloor
}) => {
  const [showAddSingleModal, setShowAddSingleModal] = useState(false);
  const [syncedRoomTypes, setSyncedRoomTypes] = useState(roomTypes);
  const [syncedBedTypes, setSyncedBedTypes] = useState(bedTypes);

  // Re-fetch latest room types and bed types on modal open to ensure inactive states are 100% fresh
  useEffect(() => {
    if (isOpen) {
      Promise.all([
        axios.get(ENDPOINTS.GET_ALL_ROOM_TYPES).catch(() => ({ data: [] })),
        axios.get(ENDPOINTS.GET_ALL_BED_TYPES).catch(() => ({ data: [] }))
      ]).then(([rtRes, btRes]) => {
        if (Array.isArray(rtRes.data)) setSyncedRoomTypes(rtRes.data);
        if (Array.isArray(btRes.data)) setSyncedBedTypes(btRes.data);
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Build sets of inactive names (case-insensitive)
  const inactiveRoomTypeSet = new Set(
    (syncedRoomTypes && syncedRoomTypes.length > 0 ? syncedRoomTypes : roomTypes || [])
      .filter((rt) => rt.status === 'Inactive')
      .map((rt) => (rt.name || '').toLowerCase())
  );

  const inactiveBedTypeSet = new Set(
    (syncedBedTypes && syncedBedTypes.length > 0 ? syncedBedTypes : bedTypes || [])
      .filter((bt) => bt.status === 'Inactive')
      .map((bt) => (bt.name || '').toLowerCase())
  );

  // Map floors to clean numerical indexes & accurate room occupancy metrics
  const floorData = (floors || []).map((fl, idx) => {
    const flName = typeof fl === 'object' && fl !== null ? (fl.floorName || '') : String(fl || '');
    const flId = typeof fl === 'object' && fl !== null ? fl.id : null;

    let displayFloor = idx;
    if (flName.toLowerCase().includes('ground')) displayFloor = 0;
    else {
      const match = flName.match(/\d+/);
      if (match) displayFloor = parseInt(match[0], 10);
    }

    // Match rooms by floorId or floorName
    const roomsOnFloor = (rooms || []).filter((r) => {
      if (flId !== null && flId !== undefined && r.floorId !== null && r.floorId !== undefined) {
        if (String(r.floorId) === String(flId)) return true;
      }
      const rFloorName = (r.floor || '').trim().toLowerCase();
      const targetFlName = flName.trim().toLowerCase();
      return rFloorName === targetFlName || (targetFlName && targetFlName.includes(rFloorName));
    });

    let occupied = 0;
    let vacant = 0;
    let inactive = 0;

    roomsOnFloor.forEach((r) => {
      const roomTypeInactive = inactiveRoomTypeSet.has((r.roomType || '').toLowerCase());
      const bedTypeInactive = inactiveBedTypeSet.has((r.bedType || '').toLowerCase());
      const isInactive =
        r.status === 'Under Maintenance' ||
        r.status === 'Inactive' ||
        r.active === false ||
        roomTypeInactive ||
        bedTypeInactive;

      if (isInactive) {
        inactive += 1;
      } else if (r.status === 'Occupied' || (r.occupiedBeds && r.occupiedBeds > 0)) {
        occupied += 1;
      } else {
        vacant += 1;
      }
    });

    const total = roomsOnFloor.length;

    return {
      id: flId !== null && flId !== undefined ? flId : idx,
      displayFloor,
      floorName: flName,
      occupied,
      vacant,
      inactive,
      total
    };
  });

  const startingFloorNum = floorData.length > 0 ? Math.min(...floorData.map((f) => f.displayFloor)) : 0;

  return (
    <>
      <div className="manage-modal-backdrop" onClick={onClose}>
        <div className="floors-modal-master-card" onClick={(e) => e.stopPropagation()}>
          {/* Top Accent Gradient Bar */}
          <div className="floors-modal-accent-bar" />

          {/* Modal Header */}
          <div className="floors-modal-header">
            <h3>
              <FaBuilding color="#2563eb" size={18} /> Hospital Floors Overview
            </h3>
            <button className="floors-modal-close-btn" onClick={onClose} title="Close">
              <FaTimes size={16} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="floors-modal-body">
            {/* Control Bar: Metrics & Add Button */}
            <div className="floors-modal-controls">
              <div className="floors-metrics-row">
                <div className="floors-metric-label">
                  <FaLayerGroup color="#2563eb" size={14} />
                  Hospital floors: <span className="floors-metric-value">{floors.length}</span>
                </div>
                <div className="floors-metric-label">
                  Starting floor: <span className="floors-metric-value">{startingFloorNum}</span>
                </div>
              </div>

              <button
                type="button"
                className="btn-add-floor-primary"
                onClick={() => setShowAddSingleModal(true)}
              >
                <FaPlus size={11} /> Add Floor
              </button>
            </div>

            {/* Clean Synced Table */}
            <div className="floors-table-wrapper">
              <table className="floors-synced-table">
                <thead>
                  <tr>
                    <th>FLOOR</th>
                    <th>ROOMS OCCUPIED</th>
                    <th>ROOM VACANT</th>
                    <th>ROOMS INACTIVE</th>
                    <th>TOTAL ROOMS</th>
                  </tr>
                </thead>
                <tbody>
                  {floorData.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ padding: '30px', color: '#64748b', fontStyle: 'italic' }}>
                        No hospital floors available.
                      </td>
                    </tr>
                  ) : (
                    floorData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="floor-pill">🏢 {row.displayFloor}</span>
                        </td>
                        <td className="text-occupied">{row.occupied}</td>
                        <td className="text-vacant">{row.vacant}</td>
                        <td className="text-inactive" style={{ color: row.inactive > 0 ? '#dc2626' : 'inherit', fontWeight: row.inactive > 0 ? '700' : 'normal' }}>
                          {row.inactive}
                        </td>
                        <td className="text-total">{row.total}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="floors-modal-footer">
            <button type="button" className="btn-close-app" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Single Floor Sub-Modal */}
      <AddFloorModal
        isOpen={showAddSingleModal}
        onClose={() => setShowAddSingleModal(false)}
        onSaveFloor={(newFloor) => {
          onAddFloor(newFloor.name);
        }}
      />
    </>
  );
};

export default HospitalFloorsModal;
